import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ApiError, fetchJSON } from "../api/client";
import { dash, formatLate, formatTime, trainLabel } from "../api/format";
import { DirectionBadge } from "../components/Badges";
import { ErrorBanner } from "../components/ErrorBanner";
import type { StationDetail, StationLiveStatus, StationTrain, StationTrainLive } from "../api/types";

const LIVE_PATH_REFRESH_MS = 20_000;

const STATUS_ORDER: StationLiveStatus[] = [
  "at_station",
  "approaching",
  "upcoming",
  "departed",
  "passed",
  "no_live",
];

const STATUS_LABEL: Record<StationLiveStatus, string> = {
  at_station: "At station",
  approaching: "Approaching",
  upcoming: "Upcoming",
  departed: "Departed",
  passed: "Passed",
  no_live: "No live GPS",
};

function liveStatus(train: StationTrain): StationLiveStatus {
  const raw = train.live?.status?.toLowerCase();
  if (raw && (STATUS_ORDER as string[]).includes(raw)) return raw as StationLiveStatus;
  return "no_live";
}

function delayLabel(live: StationTrainLive | null | undefined): string {
  if (!live || live.status === "no_live") return "no live GPS";
  if (live.delay_minutes == null) {
    const p = live.punctuality?.toLowerCase();
    if (p === "on_time") return "on time";
    if (p === "delayed") return "delayed";
    if (p === "early") return "early";
    return "—";
  }
  return formatLate(live.delay_minutes);
}

function clockPair(scheduled: string | null | undefined, expected: string | null | undefined): string {
  const left = formatTime(scheduled);
  const right = expected ? formatTime(expected) : null;
  if (!right || right === "—") return left;
  if (left === right) return left;
  return `${left} → ${right}`;
}

function StatusBadge({ status }: { status: StationLiveStatus }) {
  const cls =
    status === "at_station"
      ? "pill pill-ok"
      : status === "approaching"
        ? "pill pill-warn"
        : status === "upcoming"
          ? "pill pill-info"
          : status === "departed"
            ? "pill"
            : status === "passed"
              ? "pill pill-muted"
              : "pill pill-muted";
  return <span className={cls}>{STATUS_LABEL[status]}</span>;
}

export function StationDetailPage() {
  const { id } = useParams();
  const stationId = Number(id);
  const path = Number.isInteger(stationId) && stationId > 0 ? `/v1/stations/${stationId}?live=true` : null;

  const [data, setData] = useState<StationDetail | null>(null);
  const [error, setError] = useState<ApiError | Error | null>(null);
  const [loading, setLoading] = useState(Boolean(path));
  const [tick, setTick] = useState(0);
  const retry = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    if (!path) {
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    let inFlight = false;

    const load = async (refresh: boolean) => {
      if (inFlight) return;
      inFlight = true;
      if (!refresh) setLoading(true);
      try {
        const next = await fetchJSON<StationDetail>(path);
        if (cancelled) return;
        setData(next);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof ApiError ? err : new ApiError(String(err), "internal", 0));
        if (!refresh) setData(null);
      } finally {
        inFlight = false;
        if (!cancelled) setLoading(false);
      }
    };

    void load(false);
    const timer = window.setInterval(() => {
      void load(true);
    }, LIVE_PATH_REFRESH_MS);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [path, tick]);

  const groups = useMemo(() => {
    const trains = Array.isArray(data?.trains) ? data.trains : [];
    return STATUS_ORDER.map((status) => ({
      status,
      trains: trains.filter((t) => liveStatus(t) === status),
    })).filter((g) => g.trains.length > 0);
  }, [data]);

  if (!path) {
    return (
      <div className="page">
        <ErrorBanner error={new Error("Invalid station id")} />
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <Link to="/stations" className="muted">
            ← Stations
          </Link>
          <h1>{data?.station_name || "Station"}</h1>
          <p className="muted">
            {dash(data?.city)}
            {data?.station_name_ur ? ` · ${data.station_name_ur}` : ""}
          </p>
        </div>
      </div>
      <ErrorBanner error={error} onRetry={retry} />
      {loading && !data ? <p className="muted">Loading live board…</p> : null}
      {data ? (
        <>
          <p className="muted">
            {data.latitude != null && data.longitude != null ? `${data.latitude}, ${data.longitude}` : "No coordinates"}
          </p>
          <p className="muted">
            Live board from GPS inference — not official platform actuals. Refreshes every 20s. Delay uses live.delay_minutes, not the cached timetable field.
          </p>
          {groups.length === 0 ? <p className="muted">No trains listed for this station.</p> : null}
          {groups.map((g) => (
            <section key={g.status} className="board-group">
              <h2>
                {STATUS_LABEL[g.status]} <span className="muted">({g.trains.length})</span>
              </h2>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Train</th>
                      <th>Dir</th>
                      <th>Arrival</th>
                      <th>Departure</th>
                      <th>Delay</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.trains.map((t) => {
                      const status = liveStatus(t);
                      const live = t.live ?? null;
                      const schedArr = live?.scheduled_arrival ?? t.arrival_time;
                      const schedDep = live?.scheduled_departure ?? t.departure_time;
                      return (
                        <tr key={`${t.train_id}-${t.order_number}`}>
                          <td>
                            <Link to={`/trains/${t.train_id}`}>{trainLabel(t)}</Link>
                          </td>
                          <td>
                            <DirectionBadge direction={t.direction} />
                          </td>
                          <td>{clockPair(schedArr, live?.expected_arrival)}</td>
                          <td>{clockPair(schedDep, live?.expected_departure)}</td>
                          <td>{delayLabel(live)}</td>
                          <td>
                            <StatusBadge status={status} />
                          </td>
                          <td>
                            <Link to={`/map/${t.train_id}`}>Track</Link>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          ))}
        </>
      ) : null}
    </div>
  );
}
